import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import toast from 'react-hot-toast';
import { HiOutlineUserGroup, HiOutlineCheck, HiOutlineX } from 'react-icons/hi';

const timeAgo = (d) => {
  if (!d) return '';
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Ticket reassignment approval bell for the Super Admin panel.
 *
 * A 'support'-role admin's change to a ticket's assignees doesn't apply
 * immediately — it creates a pending TicketReassignmentRequest instead (see
 * routes/superAdminRoutes.js PATCH /support/tickets/:id). This surfaces
 * those requests and, for a full super_admin, lets them approve/reject
 * right here. Deliberately a separate component from RenewalNotificationBell
 * (different data, different action) even though the shell is similar.
 */
const ReassignmentRequestBell = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useSuperAdmin();
  const [isOpen, setIsOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const containerRef = useRef(null);

  const { data: summaryData } = useQuery({
    queryKey: ['reassignment-request-summary'],
    queryFn: superAdminAPI.getReassignmentRequestSummary,
    refetchInterval: 60000,
  });
  const pending = summaryData?.data?.data?.pending ?? 0;

  const { data: listData, isLoading } = useQuery({
    queryKey: ['reassignment-requests', 'pending'],
    queryFn: () => superAdminAPI.getReassignmentRequests({ status: 'pending', limit: 30 }),
    refetchInterval: 60000,
    enabled: isOpen,
  });
  const requests = Array.isArray(listData?.data?.data) ? listData.data.data : [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['reassignment-request-summary'] });
    queryClient.invalidateQueries({ queryKey: ['reassignment-requests'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin-ticket'] });
  };

  const approveMutation = useMutation({
    mutationFn: (id) => superAdminAPI.approveReassignmentRequest(id, {}),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Reassignment approved');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }) => superAdminAPI.rejectReassignmentRequest(id, { notes }),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Reassignment rejected');
      setRejectingId(null);
      setRejectReason('');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to reject'),
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setRejectingId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="relative p-2.5 rounded-xl bg-white border border-gray-300
                   text-gray-600 hover:bg-gray-50 transition-colors"
        title="Ticket reassignment requests"
      >
        <HiOutlineUserGroup className="w-5 h-5" />
        {pending > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center
                       text-[10px] font-bold rounded-full bg-amber-500 text-white shadow"
          >
            {pending > 99 ? '99+' : pending}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl
                     bg-white border border-gray-200 overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">
              Reassignment Requests
              {pending > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-600">
                  {pending} pending
                </span>
              )}
            </h3>
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {isLoading ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">Loading…</p>
            ) : requests.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <HiOutlineUserGroup className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">No pending requests</p>
                <p className="text-xs text-gray-400 mt-1">
                  A support admin's ticket reassignment requests will appear here.
                </p>
              </div>
            ) : (
              requests.map((r) => {
                const summary = r.changeSummary || `Change assignees to ${r.requestedAssigneeNames.join(', ') || 'Unassigned'}`;
                const summaryLower = summary.charAt(0).toLowerCase() + summary.slice(1);
                return (
                <div key={r._id} className="px-4 py-3 space-y-2">
                  <p className="text-sm text-gray-900 leading-snug">
                    <span className="font-semibold">{r.requestedByName}</span> wants to{' '}
                    <span className="font-medium">{summaryLower}</span>{' '}
                    on Ticket <span className="font-mono text-xs">{r.ticketNumber}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Currently: {r.currentAssigneeNames.join(', ') || 'Unassigned'}
                  </p>
                  <p className="text-xs text-gray-400">{r.ticketSubject} · {timeAgo(r.createdAt)}</p>

                  {isSuperAdmin && (
                    rejectingId === r._id ? (
                      <div className="space-y-1.5">
                        <input
                          type="text"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Reason (optional)"
                          className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs"
                        />
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => rejectMutation.mutate({ id: r._id, notes: rejectReason.trim() || undefined })}
                            disabled={rejectMutation.isPending}
                            className="flex-1 bg-red-500 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-red-600 disabled:opacity-50"
                          >
                            Confirm Reject
                          </button>
                          <button
                            onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            className="px-2 text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => approveMutation.mutate(r._id)}
                          disabled={approveMutation.isPending}
                          className="flex-1 inline-flex items-center justify-center gap-1 bg-green-500 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-green-600 disabled:opacity-50"
                        >
                          <HiOutlineCheck className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => setRejectingId(r._id)}
                          className="flex-1 inline-flex items-center justify-center gap-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50"
                        >
                          <HiOutlineX className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    )
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReassignmentRequestBell;
